import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { toSentryError } from "@pps/observability";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary]", error, errorInfo);
    Sentry.captureException(toSentryError(error), {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { boundary: "global" },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorBoundaryContent 
        error={this.state.error} 
        errorInfo={this.state.errorInfo}
        onReset={this.handleReset}
        onReload={this.handleReload}
      />;
    }

    return this.props.children;
  }
}

function ErrorBoundaryContent({ 
  error, 
  errorInfo,
  onReset,
  onReload
}: { 
  error: Error | null; 
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation('layout');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t('errors.unexpectedError')}
          </h1>
          <p className="text-muted-foreground">
            {t('errors.description')}
          </p>
        </div>

        {import.meta.env.DEV && error && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
            {errorInfo && (
              <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-32">
                {errorInfo.componentStack}
              </pre>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={onReset} variant="outline">
            {t('buttons.retry')}
          </Button>
          <Button onClick={onReload}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('errors.reloadPage')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
